import React, { useEffect, useRef, useState } from "react";

const secondaryCls =
  "rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800 disabled:opacity-60";

const primaryCls =
  "rounded-lg bg-emerald-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-800 disabled:opacity-60 dark:bg-emerald-600 dark:hover:bg-emerald-500";

function RfpExportMenu({ options, onExport }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  if (!options?.length) return null;

  if (options.length === 1) {
    return (
      <button type="button" className={secondaryCls} onClick={() => onExport(options[0].id)}>
        Export {options[0].label}
      </button>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        className={secondaryCls}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        Export ▾
      </button>
      {open && (
        <div
          role="menu"
          className="absolute left-0 z-50 mt-1 min-w-[12rem] rounded-lg border border-slate-200 bg-white py-1 shadow-lg dark:border-slate-700 dark:bg-slate-900"
        >
          {options.map((opt) => (
            <button
              key={opt.id}
              type="button"
              role="menuitem"
              className="block w-full px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50 dark:text-slate-100 dark:hover:bg-slate-800"
              onClick={() => {
                setOpen(false);
                onExport(opt.id);
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function RfpJobToolbar({
  exportOptions,
  onExport,
  onExportCompliance,
  onImportExcel,
  importingExcel,
  onCheckCompliance,
  checkingCompliance,
  complianceProgressLabel,
  onRefresh,
  onReprocess,
  reprocessing,
  pipelineRunning,
  onShortcuts,
  onDelete,
  deleteDisabled,
  showRegenerateAnalysis,
  onRegenerateAnalysis,
  regeneratingAnalysis,
  regenerateAnalysisLabel,
  onDiscoverRfpScope,
  discoveringRfpScope,
  discoverRfpScopeLabel,
}) {
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="rfp-job-toolbar">
      <RfpExportMenu options={exportOptions} onExport={onExport} />
      {onExportCompliance && (
        <button
          type="button"
          data-testid="rfp-export-compliance-excel"
          className={secondaryCls}
          onClick={onExportCompliance}
        >
          Export Compliance to Excel
        </button>
      )}
      {onImportExcel && (
        <button
          type="button"
          disabled={importingExcel}
          className={secondaryCls}
          onClick={onImportExcel}
        >
          {importingExcel ? "Importing…" : "Import Excel"}
        </button>
      )}
      {onCheckCompliance && (
        <button
          type="button"
          disabled={checkingCompliance || pipelineRunning}
          data-testid="rfp-check-compliance"
          className={primaryCls}
          onClick={onCheckCompliance}
        >
          {checkingCompliance ? complianceProgressLabel || "Checking…" : "Check compliance"}
        </button>
      )}
      {showRegenerateAnalysis && (
        <button
          type="button"
          disabled={regeneratingAnalysis}
          className={secondaryCls}
          onClick={onRegenerateAnalysis}
        >
          {regeneratingAnalysis
            ? regenerateAnalysisLabel || "Generating…"
            : "Regenerate Analysis"}
        </button>
      )}
      <button
        type="button"
        disabled={pipelineRunning}
        className={secondaryCls}
        onClick={onReprocess}
      >
        {reprocessing || pipelineRunning ? "Reprocessing…" : "Reprocess PDF"}
      </button>
      <button type="button" className={secondaryCls} onClick={onRefresh}>
        Refresh
      </button>
      {onShortcuts ? (
        <button type="button" className={secondaryCls} onClick={onShortcuts}>
          Shortcuts (?)
        </button>
      ) : null}
      <button
        type="button"
        disabled={deleteDisabled}
        className="ml-auto rounded-lg border border-transparent px-3 py-1.5 text-sm font-medium text-red-700 hover:border-red-200 hover:bg-red-50 dark:text-red-300 dark:hover:border-red-900 dark:hover:bg-red-950/40 disabled:opacity-60"
        onClick={onDelete}
      >
        Delete job
      </button>
    </div>
  );
}
